// Alterations after delivery (docs/after-order-plan.md). One request
// (Alterations/{id}) covers one order and any of its garments. Each
// garment has FREE_ALTERATIONS free alterations within FIT_WINDOW_DAYS of
// delivery. The customer either writes the change (areas and inches) or
// books a call with our tailor. The whole request goes one route: our
// pickup, or the customer's own tailor. For their own tailor we pay the
// bill back in cash by UPI, up to LOCAL_ALLOWANCE for each garment; the
// admin checks the bill and pays. The same file runs in the website and a
// copy in the admin panel (admin/src/Data/alterations.js). No imports.
// A garment can also come dirty, damaged or wrong: that is reported within
// DAMAGE_WINDOW_HOURS, never uses a free alteration, and only our pickup
// fixes it (clean, repair or remake).

export const ALTERATIONS_COLLECTION = 'Alterations';
export const FREE_ALTERATIONS = 3;
export const FIT_WINDOW_DAYS = 15;
export const DAMAGE_WINDOW_HOURS = 48;
const DAY = 86400000;

export const ROUTES = {
    pickup: { label: 'Free pickup', steps: ['requested', 'approved', 'picked_up', 'altering', 'shipped_back', 'done'] },
    local: { label: 'My own tailor', steps: ['requested', 'approved', 'bill_sent', 'credited'] },
};
export const STATUS_TEXT = {
    requested: 'Requested',
    approved: 'Approved',
    picked_up: 'Picked up',
    altering: 'Being altered',
    shipped_back: 'On its way back',
    done: 'Done',
    bill_sent: 'Bill sent',
    credited: 'Paid by UPI',
    declined: 'Not possible',
    refunded: 'Refunded',
};
// What the customer should do or expect at each step.
export const STATUS_NOTE = {
    pickup: {
        requested: 'We look at the photos and reply within a day.',
        approved: 'A courier will collect it. Keep it packed.',
        picked_up: 'On its way to our atelier.',
        altering: 'Your tailor is altering it now.',
        shipped_back: 'Altered and on its way back to you.',
        done: 'Altered and delivered.',
        refunded: 'The money is on its way back to you.',
    },
    local: {
        requested: 'We look at the photos and reply within a day.',
        approved: 'Go ahead with your tailor, then send us the bill, a photo and your UPI ID.',
        bill_sent: 'We are checking the bill. The money comes to your UPI.',
        credited: 'Paid to your UPI.',
    },
};
export const isClosed = (status) => ['done', 'credited', 'declined', 'refunded'].includes(status);
// A request with damage is fixed, not altered.
export const statusText = (r, status = r && r.status) => (status === 'altering' && ((r && r.items) || []).some((it) => hasDamage(it))
    ? 'Being fixed' : STATUS_TEXT[status] || status);

// Areas a garment can be changed in, and which way each one goes.
const UPPER = ['Sleeve', 'Chest', 'Shoulder', 'Waist', 'Length', 'Collar'];
const LOWER = ['Waist', 'Length', 'Hip', 'Thigh', 'Bottom', 'Seat'];
const VEST = ['Chest', 'Waist', 'Length', 'Shoulder'];
// Matched in this order: "waistcoat" holds "coat", so the sleeveless ones
// come first.
export const AREAS = {
    waistcoat: VEST, sadri: VEST, vest: VEST, nehru: VEST,
    coat: UPPER, blazer: UPPER, jacket: UPPER, sherwani: UPPER, kurta: UPPER, shirt: UPPER, jodhpuri: UPPER, bandhgala: UPPER,
    pant: LOWER, trouser: LOWER, pajama: LOWER, churidar: LOWER, dhoti: ['Waist', 'Length'],
};
export const areasFor = (garment) => {
    const g = String(garment || '').toLowerCase();
    const key = Object.keys(AREAS).find((k) => g.includes(k));
    return key ? AREAS[key] : UPPER;
};
const LENGTHWISE = ['Sleeve', 'Length'];
export const directionsFor = (area) => (LENGTHWISE.includes(area) ? ['Shorter', 'Longer'] : ['Tighter', 'Looser']);
export const INCHES = [0.5, 1, 1.5];
// Inches to the nearest quarter: 2.25 is "2¼″", 0.5 is "½″".
const QUARTERS = ['', '¼', '½', '¾'];
export const inchText = (n) => {
    const q = Math.round((Number(n) || 0) * 4);
    const whole = Math.floor(q / 4);
    const part = QUARTERS[q % 4];
    return `${whole || (part ? '' : '0')}${part}″`;
};
export const changeText = (c) => `${c.area}: ${String(c.direction || '').toLowerCase()} ${c.unsure || !(Number(c.inches) > 0) ? '(tailor to decide how much)' : `by ${inchText(c.inches)}`}`;

// ── The easy way to say it (agreed 2026-10-28) ──
// Common problems for each kind of garment, in Hindi and English, each one
// standing for an area and a direction. Tapping one asks how much, in
// inches, or "not sure" for the tailor to decide.
const UPPER_ISSUES = [
    ['long', 'Length', 'Shorter', 'लंबा है', 'Too long'],
    ['short', 'Length', 'Longer', 'छोटा है', 'Too short'],
    ['sleeve_long', 'Sleeve', 'Shorter', 'आस्तीन लंबी है', 'Sleeves too long'],
    ['sleeve_short', 'Sleeve', 'Longer', 'आस्तीन छोटी है', 'Sleeves too short'],
    ['chest_tight', 'Chest', 'Looser', 'सीने पर टाइट है', 'Tight at the chest'],
    ['chest_loose', 'Chest', 'Tighter', 'सीने पर ढीला है', 'Loose at the chest'],
    ['belly_tight', 'Waist', 'Looser', 'पेट पर टाइट है', 'Tight at the stomach'],
    ['belly_loose', 'Waist', 'Tighter', 'पेट पर ढीला है', 'Loose at the stomach'],
    ['shoulder_loose', 'Shoulder', 'Tighter', 'कंधे गिर रहे हैं', 'Shoulders too wide'],
    ['shoulder_tight', 'Shoulder', 'Looser', 'कंधे टाइट हैं', 'Shoulders tight'],
    ['collar_tight', 'Collar', 'Looser', 'गला टाइट है', 'Collar tight'],
    ['collar_loose', 'Collar', 'Tighter', 'गला ढीला है', 'Collar loose'],
];
const LOWER_ISSUES = [
    ['long', 'Length', 'Shorter', 'लंबा है', 'Too long'],
    ['short', 'Length', 'Longer', 'छोटा है', 'Too short'],
    ['waist_tight', 'Waist', 'Looser', 'कमर टाइट है', 'Tight at the waist'],
    ['waist_loose', 'Waist', 'Tighter', 'कमर ढीला है', 'Loose at the waist'],
    ['seat_tight', 'Seat', 'Looser', 'पीछे से टाइट है', 'Tight at the seat'],
    ['thigh_tight', 'Thigh', 'Looser', 'जांघ पर टाइट है', 'Tight at the thighs'],
    ['thigh_loose', 'Thigh', 'Tighter', 'जांघ पर ढीला है', 'Loose at the thighs'],
    ['bottom_wide', 'Bottom', 'Tighter', 'मोहरी चौड़ी है', 'Bottom too wide'],
    ['bottom_narrow', 'Bottom', 'Looser', 'मोहरी पतली है', 'Bottom too narrow'],
];
const VEST_ISSUES = [
    ['long', 'Length', 'Shorter', 'लंबा है', 'Too long'],
    ['short', 'Length', 'Longer', 'छोटा है', 'Too short'],
    ['chest_tight', 'Chest', 'Looser', 'सीने पर टाइट है', 'Tight at the chest'],
    ['chest_loose', 'Chest', 'Tighter', 'सीने पर ढीला है', 'Loose at the chest'],
    ['belly_tight', 'Waist', 'Looser', 'पेट पर टाइट है', 'Tight at the stomach'],
    ['belly_loose', 'Waist', 'Tighter', 'पेट पर ढीला है', 'Loose at the stomach'],
    ['shoulder_loose', 'Shoulder', 'Tighter', 'कंधे गिर रहे हैं', 'Shoulders too wide'],
];
const asIssues = (rows, kind) => rows.map(([id, area, direction, hi, en]) => ({ id, area, direction, hi, en, kind }));
// Which drawing a garment uses: 'upper', 'lower' or 'vest'.
export const garmentKind = (garment) => {
    const list = areasFor(garment);
    if (list === LOWER || list === AREAS.dhoti) return 'lower';
    if (list === VEST) return 'vest';
    return 'upper';
};
export const issuesFor = (garment) => {
    const kind = garmentKind(garment);
    if (kind === 'vest') return asIssues(VEST_ISSUES, kind);
    if (kind === 'lower') {
        const dhoti = String(garment || '').toLowerCase().includes('dhoti');
        return asIssues(dhoti ? LOWER_ISSUES.filter((r) => ['Length', 'Waist'].includes(r[1])) : LOWER_ISSUES, kind);
    }
    return asIssues(UPPER_ISSUES, kind);
};
export const INCH_CHOICES = [0.5, 1, 1.5, 2, 2.5, 3];
export const inchHindi = (n) => inchText(n).replace('″', ' इंच');
// The change in plain Hindi and English, for the summary the customer reads.
export const changeWords = (c, issues) => {
    const issue = (issues || []).find((i) => i.id === c.issue) || { hi: c.area, en: `${c.area} ${String(c.direction || '').toLowerCase()}` };
    const amt = c.unsure || !(Number(c.inches) > 0)
        ? { hi: 'दर्ज़ी देखेगा', en: 'tailor decides' }
        : { hi: inchHindi(c.inches), en: inchText(c.inches) };
    return { hi: `${issue.hi}: ${amt.hi}`, en: `${issue.en}: ${amt.en}` };
};

// The garments in one order line, as the customer knows them.
const label = (v) => String(v || '').replace(/^only_/, '').replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
export const garmentsOf = (o) => {
    if (!o) return [];
    if (o.item_type === 0) return [];
    if (o.item_type === 1) {
        const kinds = (Array.isArray(o.fabrics) ? o.fabrics : []).map((f) => f && f.type).filter(Boolean);
        const unique = [...new Set(kinds.map(label))];
        return unique.length ? unique : [label(o.category || 'Garment')];
    }
    const types = (Array.isArray(o.types) ? o.types : [])
        .filter((t) => !(String(o.category) === 'only_kurta' && !o.buyPajama && t === 'Pajama'));
    return types.length ? types.map(label) : [label(o.category || 'Garment')];
};

// Why a line cannot be altered at all ('' when it can).
export const lineProblem = (o) => {
    if (!o) return 'Not found.';
    if (o.item_type === 0) return 'Fabric samples are not altered.';
    if (o.productKind === 'accessory' || o.measurementProfile === 'none') return 'Accessories are not altered.';
    if (o.productKind === 'uniform') return 'For uniforms, WhatsApp us and we will sort it.';
    return '';
};

const toMs = (v) => {
    if (!v) return 0;
    if (typeof v === 'number') return v;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
};
// When the line was delivered: the admin's status history, else the latest step.
export const deliveredAt = (o) => {
    const hist = Array.isArray(o && o.statusHistory) ? o.statusHistory : [];
    const d = hist.filter((h) => Number(h.status) === 4).map((h) => toMs(h.at || h.Date)).filter(Boolean);
    if (d.length) return Math.max(...d);
    return o && o.status && Number(o.status.status) === 4 ? toMs(o.status.Date) : 0;
};
// A piece that came back from our atelier (a pickup request marked done)
// starts its windows again from that day: 48 hours for damage, 15 days for
// the fit. The free alterations it has left stay the same.
const has = (r, orderDocId, garment) => (r.items || []).some((it) => String(it.orderDocId) === String(orderDocId) && it.garment === garment);
export const returnedAt = (requests, orderDocId, garment) => Math.max(0, ...(Array.isArray(requests) ? requests : [])
    .filter((r) => r && r.route === 'pickup' && r.status === 'done' && has(r, orderDocId, garment))
    .map((r) => toMs(r.returnedAt) || toMs(((r.history || []).filter((h) => h.status === 'done').pop() || {}).at)));
export const windowStart = (o, garment, requests) => Math.max(deliveredAt(o), garment ? returnedAt(requests, o && o.id, garment) : 0);
export const windowEnds = (o, garment, requests) => { const d = windowStart(o, garment, requests); return d ? d + FIT_WINDOW_DAYS * DAY : 0; };

// ── Dirty or damaged (agreed 2026-10-28) ──
export const DAMAGE_ISSUES = [
    ['stain', 'दाग़ है या गंदा है', 'Stained or dirty'],
    ['torn', 'फटा है या छेद है', 'Torn or a hole'],
    ['seam', 'सिलाई खुली है', 'Stitching come open'],
    ['button', 'बटन या ज़िप ख़राब है', 'Button or zip broken'],
    ['wrong', 'ग़लत कपड़ा या रंग', 'Wrong fabric or colour'],
].map(([id, hi, en]) => ({ id, hi, en }));
export const damageWords = (id) => DAMAGE_ISSUES.find((d) => d.id === id) || { id, hi: id, en: id };
export const damageWindowEnds = (o, garment, requests) => { const d = windowStart(o, garment, requests); return d ? d + DAMAGE_WINDOW_HOURS * 3600000 : 0; };
export const damageOpen = (o, now = Date.now(), garment, requests) => { const ends = damageWindowEnds(o, garment, requests); return !!ends && now <= ends; };
// How we put a damaged piece right: the admin chooses, the customer sees it.
export const DAMAGE_FIXES = {
    clean: { hi: 'हम साफ़ करेंगे', en: 'We clean it' },
    repair: { hi: 'हम ठीक करेंगे', en: 'We repair it' },
    remake: { hi: 'हम नया बनाएँगे', en: 'We make it again' },
    refund: { hi: 'हम पैसे लौटाएँगे', en: 'We refund it' },
};
// What a garment's request is about: 'fit', 'damage' or both. Requests made
// before damage could be reported are fit.
export const reasonsOf = (it) => (it && Array.isArray(it.reasons) ? it.reasons : ['fit']);
export const hasDamage = (it) => reasonsOf(it).includes('damage');
// One line for staff and the tracker: "Damage: Stained or dirty, Torn or a hole".
export const damageText = (it) => (hasDamage(it)
    ? `Damage: ${(it.damage || []).map((id) => damageWords(id).en).join(', ') || 'see the photos and note'}` : '');

// How many free alterations a garment has used: requests not turned down.
// Damage alone is our fault and is not counted.
export const usedFor = (requests, orderDocId, garment) => (Array.isArray(requests) ? requests : [])
    .filter((r) => r && r.status !== 'declined')
    .reduce((n, r) => n + (r.items || []).filter((it) => String(it.orderDocId) === String(orderDocId) && it.garment === garment
        && it.status !== 'declined' && reasonsOf(it).includes('fit')).length, 0);

// Why one garment cannot be chosen now ('' when it can).
export const garmentProblem = (o, garment, requests, now = Date.now()) => {
    const line = lineProblem(o);
    if (line) return line;
    if (Number(o && o.status && o.status.status) < 4 && !deliveredAt(o)) return 'Not delivered yet. Ask once it arrives.';
    if ((requests || []).some((r) => r && r.status === 'refunded' && has(r, o.id, garment))) return 'Refunded. Nothing more to alter.';
    const ends = windowEnds(o, garment, requests);
    if (ends && now > ends) return `The ${FIT_WINDOW_DAYS} days for free alterations are over.`;
    const open = (requests || []).some((r) => r && !isClosed(r.status) && has(r, o.id, garment));
    if (open) return 'Already in an open request.';
    if (usedFor(requests, o.id, garment) >= FREE_ALTERATIONS) return `All ${FREE_ALTERATIONS} free alterations used.`;
    return '';
};

// What one garment's form still needs ('' when ready).
export const itemProblem = (it) => {
    if (!it) return 'Missing.';
    if (!(it.photos || []).length) return 'Add at least one photo of it being worn.';
    const reasons = reasonsOf(it);
    if (!reasons.length) return 'Choose what is wrong: the fit, or dirty or damaged.';
    const said = String(it.note || '').trim() || it.voiceNote;
    if (reasons.includes('damage') && !(it.damage || []).length && !said) return 'Choose what is damaged, speak a voice note, or write a note.';
    if (!reasons.includes('fit')) return '';
    if (it.mode === 'call') {
        if (!it.call || !it.call.day || !it.call.slot) return 'Pick a day and a time for the call.';
        return '';
    }
    const changes = (it.changes || []).filter((c) => c.area);
    if (!changes.length && !said) return 'Choose the problem, speak a voice note, or write a note.';
    if (changes.some((c) => !c.direction || (!(Number(c.inches) > 0) && !c.unsure))) return 'Say how much for each problem, in inches, or choose not sure.';
    return '';
};

// Call slots: the next four days, four slots a day (24-hour labels).
export const CALL_SLOTS = [['11-12', '11–12'], ['13-14', '1–2'], ['16-17', '4–5'], ['18-19', '6–7']];
export const callDays = (now = new Date(), count = 4) => Array.from({ length: count }, (_, i) => {
    const d = new Date(now); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { iso, label: i === 0 ? 'Today' : d.toLocaleDateString('en-IN', { weekday: 'short' }), date: d.getDate() };
});
// Pickup: the customer picks a day (from tomorrow) and half a day.
export const PICKUP_SLOTS = [['morning', 'सुबह 10–2', 'Morning 10–2'], ['evening', 'शाम 2–7', 'Afternoon 2–7']];
export const pickupDays = (now = new Date()) => callDays(now, 5).slice(1);
export const pickupText = (p) => {
    if (!p || !p.day) return '';
    const [y, m, d] = String(p.day).split('-').map(Number);
    const day = new Date(y, m - 1, d).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    const slot = PICKUP_SLOTS.find(([k]) => k === p.slot);
    return slot ? `${day} · ${slot[2]}` : day;
};
export const slotLabel = (slot) => (CALL_SLOTS.find(([k]) => k === slot) || [slot, slot])[1];
// Today's slots that have already started cannot be booked.
export const slotOpen = (dayIso, slot, now = new Date()) => {
    const [h] = String(slot).split('-').map(Number);
    const [y, m, d] = String(dayIso).split('-').map(Number);
    return new Date(y, m - 1, d, h, 0, 0).getTime() > now.getTime() + 30 * 60000;
};

// Own tailor: what we pay back for each garment at most (agreed 2026-10-28),
// in cash by UPI.
export const LOCAL_ALLOWANCE = { coat: 500, jacket: 300, pant: 200, shirt: 200, kurta: 200, pajama: 200, vest: 200 };
// Checked in this order: "waistcoat" holds "coat", so vests come first.
const ALLOWANCE_KIND = [
    ['vest', ['vest', 'waistcoat']],
    ['jacket', ['jacket', 'nehru', 'sadri']],
    ['coat', ['coat', 'blazer', 'sherwani', 'bandhgala', 'jodhpuri', 'tuxedo', 'achkan']],
    ['pajama', ['pajama', 'churidar', 'dhoti', 'salwar']],
    ['pant', ['pant', 'trouser']],
    ['shirt', ['shirt']],
    ['kurta', ['kurta']],
];
export const allowanceFor = (garment) => {
    const g = String(garment || '').toLowerCase();
    const hit = ALLOWANCE_KIND.find(([, words]) => words.some((w) => g.includes(w)));
    return LOCAL_ALLOWANCE[hit ? hit[0] : 'shirt'];
};
export const allowanceCap = (items) => (Array.isArray(items) ? items : []).filter((it) => it && it.status !== 'declined').reduce((n, it) => n + allowanceFor(it.garment), 0);
// What to pay for a bill: the bill, up to the cap (the cap when no amount is given).
export const suggestedPayout = (items, billAmount) => {
    const cap = allowanceCap(items);
    const bill = Math.round(Number(billAmount) || 0);
    return bill > 0 ? Math.min(bill, cap) : cap;
};
export const UPI_RE = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z][a-zA-Z0-9]{1,64}$/;

// What we tell the customer when a request moves on (email and WhatsApp),
// in Hindi and English.
const UPDATE_LINES = {
    approved_pickup: ['आपका अनुरोध मंज़ूर है। कूरियर कपड़ा लेने आएगा, उसे पैक रखें।', 'Approved. A courier will collect it. Please keep it packed.'],
    approved_local: ['आपका अनुरोध मंज़ूर है। अपने दर्ज़ी से ठीक कराएँ, फिर ऑर्डर पेज से बिल, पहनकर फ़ोटो और UPI ID भेजें।', 'Approved. Go ahead with your tailor, then send the bill, a photo of it worn and your UPI ID from your order page.'],
    picked_up: ['कपड़ा ले लिया गया है और हमारे एटेलियर जा रहा है।', 'Picked up and on its way to our atelier.'],
    altering: ['हमारे दर्ज़ी अभी इस पर काम कर रहे हैं।', 'Our tailor is working on it now.'],
    shipped_back: ['तैयार है और आपके पास वापस आ रहा है।', 'Ready and on its way back to you.'],
    done: ['आपके पास पहुँच गया। कोई ख़राबी हो तो 48 घंटे में, फ़िटिंग ठीक न हो तो 15 दिन में ऑर्डर पेज से बताएँ।', 'Delivered back to you. If anything is damaged, tell us within 48 hours; if the fit is still off, within 15 days, from your order page.'],
    declined: ['माफ़ कीजिए, यह इस तरह नहीं हो सकता। हम आपको कॉल करके दूसरा रास्ता बताएँगे।', 'Sorry, this cannot be done this way. We will call you with another way.'],
};
export const updateMessage = (r, status = r && r.status) => {
    const key = status === 'approved' ? `approved_${r.route === 'local' ? 'local' : 'pickup'}` : status;
    const money = (x) => (x ? `₹${Number(x.amount || 0).toLocaleString('en-IN')}${x.ref ? ` (ref ${x.ref})` : ''}` : '');
    const upi = (r.paid && r.paid.upi) || r.upi || '';
    const line = status === 'credited' ? [`${money(r.paid)} आपके UPI ${upi} पर भेज दिए गए।`, `${money(r.paid)} sent to your UPI ${upi}.`]
        : status === 'refunded' ? [`${money(r.refund)} आपको वापस भेज दिए गए।`, `We have refunded ${money(r.refund)} to you.`]
            : UPDATE_LINES[key] || ['', statusText(r, status)];
    const fixes = (r.items || []).filter((it) => hasDamage(it) && DAMAGE_FIXES[it.fix])
        .map((it) => `${it.garment}: ${DAMAGE_FIXES[it.fix].hi} · ${DAMAGE_FIXES[it.fix].en}`);
    const pieces = (r.items || []).map((it) => it.garment).join(', ');
    const text = [
        `Order ${r.orderId || ''} · ${pieces}`,
        line[0], line[1],
        ...fixes,
        status === 'approved' && r.route === 'pickup' && r.pickup ? `Pickup: ${pickupText(r.pickup)}` : '',
        r.reply ? `From our tailor: ${r.reply}` : '',
    ].filter(Boolean).join('\n');
    return { subject: `Your alteration: ${statusText(r, status)}`, text };
};

// ── Reviews after delivery (agreed 2026-10-28; 1000 coins for any review, 2026-10-29) ──
// A delivered piece can be reviewed once, from its order. Every review earns
// REVIEW_COINS once the admin approves it (credited by the admin panel, once
// per piece). The offer hides while an alteration on that order is open and
// comes back when the piece is with the customer again.
export const REVIEWS_COLLECTION = 'Reviews';
export const REVIEW_COINS = 1000;
// One review per piece: the review's id is the order line's, so a second
// one would be an update, which only the admin panel may make.
export const reviewId = (line) => `line-${line && line.id}`;
export const alterationOpen = (requests, orderId) => (Array.isArray(requests) ? requests : [])
    .some((r) => r && r.orderId === orderId && !isClosed(r.status));
// The customer's review of one order line, if there is one.
export const reviewOf = (reviews, line) => (Array.isArray(reviews) ? reviews : []).find((v) => v && line
    && (v.orderDocId ? String(v.orderDocId) === String(line.id) : v.orderId === (line.orderId || line.id)));
export const earnsCoins = (review) => !!review && !!review.account;
export const reviewCoinsDue = (review) => earnsCoins(review) && review.status === 'approved' && !review.coins;
// The wallet ledger line for a piece's review coins: one per piece, even if
// an older piece has more than one review.
export const reviewLedgerId = (review) => `review-${(review && (review.orderDocId || review.id)) || ''}`;

// ── Personal accounts only (agreed 2026-10-28) ──
// Alterations and reviews are for personal accounts. A business account
// (verified, or paused) and an order placed as a business have their own
// flow, so neither is offered there.
export const isBusinessAccount = (biz) => !!biz && ['verified', 'paused'].includes(biz.status);
export const isBusinessOrder = (line) => !!(line && line.offerCheck && line.offerCheck.business);
export const personalCare = (line, biz) => !isBusinessAccount(biz) && !isBusinessOrder(line);

